import { UserName } from "../components/UserName.tsx";
import { DuelClock } from "../components/DuelClock.tsx";
import { GhostBoard } from "../components/GhostBoard.tsx";
import { ResultBoard } from "../components/ResultBoard.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { WordMeaning } from "../components/WordMeaning.tsx";
import { duelRulesSummary } from "../features/duel/rules.ts";
import { useSpectate } from "../features/duel/useSpectate.ts";
import { messages } from "../i18n/index.ts";

interface SpectateScreenProps {
  matchId: string;
  onBack: () => void;
  onFollowMatch: (matchId: string) => void;
}

/**
 * Arkadaşının maçını izleme.
 *
 * Ne kadarını gördüğünü sunucu OYUNCU BAZINDA belirliyor: arkadaşın olanın
 * tahtası harfleriyle, tanımadığın rakibinki yalnızca renkleriyle gelir.
 * Yetkin yoksa harf sunucudan hiç gönderilmiyor — istemcide gizlemek gerçek
 * bir koruma sayılmaz.
 */
export function SpectateScreen({ matchId, onBack, onFollowMatch }: SpectateScreenProps) {
  const spectate = useSpectate(matchId, onFollowMatch);

  if (spectate.status === "loading") {
    return (
      <div className="app">
        <ScreenHeader title={messages.spectate.title} onBack={onBack} />
        <main className="grid place-items-center">
          <p className="text-sm text-muted">{messages.spectate.connecting}</p>
        </main>
      </div>
    );
  }

  if (spectate.status === "error") {
    return (
      <div className="app">
        <ScreenHeader title={messages.spectate.title} onBack={onBack} />
        <main className="grid place-items-center px-6 text-center">
          <div>
            <p className="font-display text-lg font-bold">{messages.spectate.failed}</p>
            <p className="mt-2 text-sm text-muted">{spectate.error}</p>
            <button type="button" className="btn-secondary mt-5" onClick={onBack}>
              {messages.spectate.return}
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------- Maç bitti ---------------- */

  if (spectate.status === "over" && spectate.result) {
    const { result } = spectate;
    const winner = result.players.find((player) => player.user.id === result.winnerId);

    return (
      <div className="app">
        <ScreenHeader title={messages.spectate.title} onBack={onBack} />
        <main className="overflow-y-auto px-4 py-5">
          <div className="mx-auto flex w-full max-w-md flex-col gap-5">
            <div className="text-center">
              <span className="eyebrow">{messages.spectate.matchOver}</span>
              <p className="scoreboard mt-1 text-[2rem]">
                {winner ? messages.spectate.winner(winner.user.username) : messages.spectate.draw}
              </p>
            </div>

            <div className="answer-row">
              {[...result.answer].map((letter, i) => (
                <span key={i} className="tile" data-filled="true" data-state="correct">
                  {letter}
                </span>
              ))}
            </div>

            <WordMeaning word={result.answer} />

            <p className="text-center text-xs text-muted">
              {result.category === "friendly" ? messages.common.friendlyMatch : messages.common.standardDuel} ·{" "}
              {duelRulesSummary(result.rules)}
            </p>

            <div className="duel-result-grid">
              {result.players.map((player, index) => (
                <div
                  key={player.user.id}
                  className={`duel-result-card ${index === 0 ? "plate-l" : "plate-r"}`}
                >
                  <span className="duel-compare__name">
                    {<UserName user={player.user} />}{" "}
                    <span className={player.solved ? "text-mint" : "text-muted"}>
                      · {player.attempts}
                    </span>
                  </span>
                  <ResultBoard rows={player.guesses} />
                </div>
              ))}
            </div>

            <button type="button" className="btn-primary" onClick={onBack}>
              {messages.spectate.backToFriends}
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------- Canlı ---------------- */

  const state = spectate.state;
  if (!state) return null;

  /*
   * Not metni veriden türüyor: hangi tahtada harf varsa onu söylüyoruz.
   * Ayrı bir "yetki seviyesi" alanı taşımıyoruz, tek doğruluk kaynağı paket.
   */
  const withLetters = state.players.filter((player) => player.guesses !== null);
  const letterNote =
    withLetters.length === state.players.length
      ? messages.spectate.bothLetters
      : withLetters.length > 0
        ? messages.spectate.oneLetters(withLetters[0]!.user.username)
        : messages.spectate.colorsOnly;

  return (
    <div className="app">
      <ScreenHeader
        title={messages.spectate.title}
        onBack={onBack}
        actions={
          <span className="flex items-center gap-1.5">
            <span className="live-dot" aria-hidden="true" />
            <span className="eyebrow eyebrow--live">{messages.spectate.live}</span>
          </span>
        }
      />

      <main className="overflow-y-auto px-4 pb-5">
        <div className="mx-auto flex w-full max-w-md flex-col gap-4">
          <div className="spectate-clock">
            <DuelClock remainingMs={spectate.remainingMs} />
          </div>

          <p className="text-center text-xs text-muted">{duelRulesSummary(state.rules)}</p>

          <div className="spectate-grid">
            {state.players.map((player, index) =>
              player.guesses ? (
                /* İki oyuncu da arkadaşın: tahtayı harfleriyle görüyorsun. */
                <div
                  key={player.user.id}
                  className={`duel-result-card ${index === 0 ? "plate-l" : "plate-r"}`}
                >
                  <span className="duel-compare__name">
                    {<UserName user={player.user} />}{" "}
                    <span className={player.solved ? "text-mint" : "text-muted"}>
                      · {player.attempts}/{state.rules.maxAttempts}
                    </span>
                  </span>
                  {/* Kalan haklar boş kutu olarak duruyor; yanındaki hayalet
                      tahta da altı satır çiziyor, ikisi aynı boyda kalsın. */}
                  <ResultBoard
                    rows={player.guesses}
                    padTo={state.rules.maxAttempts}
                    draft={player.draft}
                    attemptLimit={player.attemptLimit}
                  />
                </div>
              ) : (
                <GhostBoard
                  key={player.user.id}
                  rows={player.rows}
                  label={player.user.username}
                  solvedAt={player.solved ? player.attempts : null}
                  attempts={player.attempts}
                  attemptLimit={player.attemptLimit}
                  maxAttempts={state.rules.maxAttempts}
                />
              ),
            )}
          </div>

          <p className="spectate-note" role="status">
            {letterNote}
            {state.players.every((player) => player.attempts === 0) &&
              messages.spectate.preparing}
          </p>

          <p className="text-center text-xs text-muted">
            {state.players.map((player, index) => (
              <span key={player.user.id}>{index > 0 && "  ·  "}<UserName user={player.user} /> {player.attempts}/{state.rules.maxAttempts}</span>
            ))}
          </p>
        </div>
      </main>
    </div>
  );
}
